import { ReactNode, useEffect, useState } from 'react';

export type SortOrder = 'default' | 'title';
export type ViewMode = 'list' | 'grid';

/**
 * 'default' passes items through in whatever order they arrived in (most-recent-first from the
 * library/shared-library queries; a playlist's own stored order for playlist videos) — no shared
 * "recency" concept makes sense across all three lists, so this is the one sort order every list
 * can offer without a special case.
 */
export function sortItems<T>(items: T[], order: SortOrder, getTitle: (item: T) => string): T[] {
  if (order !== 'title') return items;
  return [...items].sort((a, b) => getTitle(a).localeCompare(getTitle(b), undefined, { sensitivity: 'base' }));
}

export function filterByTitle<T>(items: T[], search: string, getTitle: (item: T) => string): T[] {
  const q = search.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => getTitle(item).toLowerCase().includes(q));
}

/** Persists the chosen view mode to localStorage under a distinct key per list, so each of the
 * library/shared-library/playlist pages remembers its own preference independently. */
export function useViewMode(listKey: string): [ViewMode, (mode: ViewMode) => void] {
  const storageKey = `vod-frontend:view:${listKey}`;
  const [mode, setModeState] = useState<ViewMode>(() => {
    try {
      return window.localStorage.getItem(storageKey) === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  const setMode = (next: ViewMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(storageKey, next);
    } catch {
      // Storage can be unavailable (private browsing, quota) — the in-memory choice still works
      // for the rest of this session, it just won't persist.
    }
  };
  return [mode, setMode];
}

export interface Selection<T> {
  selectedIds: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Adds every one of `items` to the current selection (a union, not a replace) — an item
   * selected earlier and since hidden by a narrower search stays selected; "select all" only ever
   * grows the selection to also include whatever currently passes the filter. */
  selectAll: (items: T[]) => void;
  clear: () => void;
  count: number;
}

/** Selection state for one list surface, keyed by whatever id the caller's items use. Deliberately
 * plain component-local state (not persisted) — selection is a per-visit interaction, not
 * something that should survive a reload or follow the user to another page. */
export function useSelection<T>(idOf: (item: T) => string): Selection<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  return {
    selectedIds,
    isSelected: (id) => selectedIds.has(id),
    toggle: (id) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    selectAll: (items) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        items.forEach((item) => next.add(idOf(item)));
        return next;
      }),
    clear: () => setSelectedIds(new Set()),
    count: selectedIds.size,
  };
}

/** Toggles a page in and out of bulk-selection mode. Rendered inline by `ListToolbar` for the
 * three pages that already use it, and standalone by pages (e.g. the Playlists list) that don't. */
export function SelectionModeToggle({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button className={active ? undefined : 'secondary'} onClick={onToggle} aria-pressed={active}>
      {active ? 'Done selecting' : 'Select'}
    </button>
  );
}

/** The bar shown while selection mode is active: selection count, select-all/clear, and whatever
 * bulk-action buttons the calling page passes in as children. */
export function SelectionBar({
  count,
  onSelectAll,
  onClear,
  children,
}: {
  count: number;
  onSelectAll: () => void;
  onClear: () => void;
  children?: ReactNode;
}) {
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: 'var(--space-4)',
      }}
    >
      <strong>{count} selected</strong>
      <button className="secondary" onClick={onSelectAll}>
        Select all
      </button>
      <button className="secondary" onClick={onClear} disabled={count === 0}>
        Clear selection
      </button>
      {children}
    </div>
  );
}

export function SortControl({ value, onChange }: { value: SortOrder; onChange: (v: SortOrder) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as SortOrder)}
      aria-label="Sort order"
    >
      <option value="default">Default order</option>
      <option value="title">Title (A–Z)</option>
    </select>
  );
}

export function ViewModeToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  return (
    <div style={{ display: 'inline-flex', gap: 4 }}>
      <button
        className={mode === 'list' ? undefined : 'secondary'}
        onClick={() => onChange('list')}
        aria-pressed={mode === 'list'}
        aria-label="List view"
      >
        List
      </button>
      <button
        className={mode === 'grid' ? undefined : 'secondary'}
        onClick={() => onChange('grid')}
        aria-pressed={mode === 'grid'}
        aria-label="Grid view"
      >
        Grid
      </button>
    </div>
  );
}

/** Debounces the propagated search term so filtering a large in-memory list doesn't re-run on
 * every keystroke; the input itself stays fully responsive since its own value is local state. */
export function SearchBox({
  onSearch,
  placeholder,
}: {
  onSearch: (value: string) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => onSearch(text), 150);
    return () => clearTimeout(timer);
    // onSearch is expected to be stable (a setState setter or equivalent) across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <input
      type="search"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder ?? 'Search…'}
      aria-label="Search"
      style={{ minWidth: 200 }}
    />
  );
}

/** Shared toolbar row combining sort, view-mode, and search — used identically by the library,
 * shared library, and playlist pages. */
export function ListToolbar({
  sort,
  onSortChange,
  viewMode,
  onViewModeChange,
  onSearch,
  searchPlaceholder,
  selectionMode,
  onToggleSelectionMode,
}: {
  sort: SortOrder;
  onSortChange: (v: SortOrder) => void;
  viewMode: ViewMode;
  onViewModeChange: (m: ViewMode) => void;
  onSearch: (v: string) => void;
  searchPlaceholder?: string;
  /** Both provided together enable a "Select" toggle at the end of the toolbar; omit both on
   * pages that don't offer bulk selection. */
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: 'var(--space-4)',
      }}
    >
      <SearchBox onSearch={onSearch} placeholder={searchPlaceholder} />
      <SortControl value={sort} onChange={onSortChange} />
      <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
      {onToggleSelectionMode && (
        <SelectionModeToggle active={!!selectionMode} onToggle={onToggleSelectionMode} />
      )}
    </div>
  );
}
